import React, { useMemo } from 'react';

import { Box, Chip, SvgIconProps, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PersonOutline, VideocamOutlined } from '@mui/icons-material';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { ListItem, ProviderMedicalRole, SeeClientsEnum, UserRole } from '@uplift/types';
import { UserImage } from '@uplift/ui';

import { useSelectedProviderContext } from '../selected-provider.context';
import { SelectedProviderSubSectionEnum } from '../types';
import { useCurrentuser } from '@uplift/contexts';

export type ProviderHeaderProps = {
  forceMobile?: boolean;
  stateId: string;
};

type ChipConfig = {
  label: string;
  icon: React.ComponentType<SvgIconProps>;
};

export const useProviderRoleTitleLabel = (
  medicalRole: ProviderMedicalRole,
  mentalHealthRoles: ListItem[],
  licenses: (ListItem & { stateId: string })[],
  stateId: string
) => {
  const l10n = useL10n();

  return React.useMemo(() => {
    const mentalHealthRolesSet = new Set(mentalHealthRoles.map(c => c.name));
    const licenseTypeSet = new Set(licenses.map(l => l.name));

    if (medicalRole === ProviderMedicalRole.psychiatrist) {
      if (mentalHealthRolesSet.has('MD') || mentalHealthRolesSet.has('DO')) {
        return l10n.resources.provider.types.psychiatrist;
      }

      if (mentalHealthRolesSet.has('PMHNP')) {
        return l10n.resources.provider.types.pmhnp;
      }
      return licenses.find(l => l.stateId === stateId)?.name ?? l10n.resources.provider.types.psychiatricProvider;
    }
    if (licenseTypeSet.has('Psychologist')) {
      return l10n.resources.provider.types.psychologist;
    }

    return l10n.resources.provider.types.therapist;
  }, [ mentalHealthRoles, licenses, medicalRole, l10n.resources.provider.types, stateId ]);
};

export const ProviderHeader: React.VFC<ProviderHeaderProps> = (props) => {
  const isMobile = useIsMobile();
  const { selectedProvider: { medicalRole, userProfile, nameDisplay } } = useSelectedProviderContext();
  const { classes } = useProviderHeaderStyles(props, {
    props: props
  });
  const { avatarAsset, pronouns } = userProfile;
  const me = useCurrentuser();
  const providerRoleLabel = useProviderRoleTitleLabel(
    medicalRole,
    userProfile.mentalRoles,
    userProfile.license.map(l => ({ id: l.id, name: l.name, stateId: l.state?.id })),
    props.stateId,
  );

  const providerName = useMemo(() => {
    if (me.role === UserRole.provider) {
      return nameDisplay.fullWithAffixes;
    }
    return nameDisplay.defaultDisplay;
  },[ me.role, nameDisplay.defaultDisplay, nameDisplay.fullWithAffixes ]);

  const chips = React.useMemo(() => {
    const serviceType = userProfile?.practice?.practiceServiceType;
    const chips: ChipConfig[] = [];

    if (serviceType === SeeClientsEnum.telehealth) {
      chips.push({
        label: 'Virtual',
        icon: VideocamOutlined
      });
    } else if (serviceType === SeeClientsEnum.inPerson) {
      chips.push({
        label: 'In-person',
        icon: PersonOutline
      });
    }

    return chips;
  }, [ userProfile?.practice?.practiceServiceType ]);

  return (
    <Box className={classes.header} id={SelectedProviderSubSectionEnum.header}>
      <Box flex="0 0 auto">
        <UserImage imageUrl={avatarAsset?.url} maxWidth={isMobile || props.forceMobile ? '72px' : '104px'} />
      </Box>
      <Box className={classes.contentContainer}>
        <Typography className={classes.name}>{providerName}</Typography>
        {pronouns && <Typography className={classes.pronouns} component="span">({pronouns})</Typography>}
        <Box className={classes.labelBox}>
          <Typography color="textSecondary">{providerRoleLabel}</Typography>
          {chips.map(({ label, icon: Icon }, idx) => (
            <Chip
              size="small"
              icon={<Icon fontSize="small"/>}
              label={label}
              className={classes.serviceTypeChip}
              key={idx}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const useProviderHeaderStyles = makeStyles<{ forceMobile?: boolean }>({ name: 'ProviderHeader' })((theme, { forceMobile }) => ({
  header: {
    display: 'flex',
    gridGap: '1rem',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  contentContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column'
  },
  name: ({
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '1.1',
    marginRight: theme.spacing(0.5),
    display: 'flex',

    ...(!forceMobile && ({
      [theme.breakpoints.up('sm')]: {
        fontSize: '24px',
      }
    }))
  }),
  pronouns: ({
    fontSize: '14px',
    color: theme.palette.text.secondary,

    ...(!forceMobile && ({
      [theme.breakpoints.up('sm')]: {
        fontSize: '16px'
      }
    }))
  }),
  serviceTypeChip: {
    backgroundColor: theme.palette.info.background,
    marginRight: theme.spacing(1),
    paddingRight: theme.spacing(0.25),
    paddingLeft: theme.spacing(1),
    '& svg': {
      color: theme.palette.info.main,
      marginLeft: 0,
    }
  },
  labelBox: {
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      marginBottom: theme.spacing(0.5),
    },
    display: 'flex',
    flexDirection: 'column',
    gridGap: '4px',
    '& > div': {
      width: 'fit-content'
    }
  }
}));
